import { RadioCard, Tooltip } from "@blurb/codex-react";
import { Box, createStyles } from "@mantine/core";
import { RpiLabelText } from "@rpi/openapi-core";
import { getCardIconPath, getDisplayName } from "./attributeValueMappings";

export type IconCardConfig = {
  value: string;
  displayName: string;
  iconPath: string;
  disabled?: boolean;
  renderTooltip?: () => JSX.Element;
};

const useIconCardStyles = createStyles((theme) => ({
  radioCard: {
    width: 96,
    height: 128,
    borderRadius: theme.radius.sm,
    justifyContent: "flex-start",
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    padding: `${theme.spacing.xs}px 0`,
    border: `1px solid ${theme.other.semanticColors.border.subtle}`,
  },
  cardContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: theme.spacing.xs,
  },
  image: {
    height: 88,
    width: 88,
  },
  labelContainer: {
    marginTop: theme.spacing.xs,
    textAlign: "center",
    width: "100%",
    "& *": {
      fontSize: `${theme.fontSizes.sm}px !important`,
    },
  },
}));

export const defaultGetIconCardConfig =
  (attributeKey: string): ((value: string) => IconCardConfig | undefined) =>
  (value) => {
    if (!attributeKey) return undefined;

    const iconPath = getCardIconPath(attributeKey, String(value));
    const displayName = getDisplayName(attributeKey, String(value));

    if (!iconPath || !displayName) return undefined;

    return {
      value: String(value),
      displayName,
      iconPath,
      disabled: false,
    };
  };

export const IconCard = ({ value, displayName, iconPath, disabled = false, renderTooltip }: IconCardConfig) => {
  const { classes } = useIconCardStyles();

  const card = (
    <RadioCard className={classes.radioCard} value={value} disabled={disabled} data-testid={value}>
      <Box className={classes.cardContent}>
        <img className={classes.image} src={iconPath} alt={displayName} />
      </Box>
      <Box className={classes.labelContainer}>
        <RpiLabelText>{displayName}</RpiLabelText>
      </Box>
    </RadioCard>
  );

  if (renderTooltip) {
    // A disabled RadioCard doesn't emit pointer events, so the tooltip anchors to a wrapper instead.
    return (
      <Tooltip content={renderTooltip()}>
        <Box sx={{ display: "inline-block" }}>{card}</Box>
      </Tooltip>
    );
  }

  return card;
};
